'use client';

/**
 * RailContextHeader — surface-level anchor for the Insights rail.
 *
 * Pure renderer. Reads three pre-composed strings from
 * `selectedNode.properties` — `header_type_label`, `header_primary`,
 * `header_subline` — that the BFF shapes per entity at fetch time
 * (`projects/handler.py: _shape_rfq_header` etc.). No date formatting,
 * no string composition, no per-entity-type conditionals here. The
 * card just paints what the BFF ships.
 *
 * Falls back to `selectedNode.label` for the primary line when the
 * BFF hasn't shipped a `header_primary` (e.g. selections from
 * surfaces that don't yet plumb header_* fields). Header is hidden
 * entirely when nothing is selected.
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain copilot
 */

import { useViewportSelection } from '@/lib/stores/viewport-selection';
import { cn } from '@/lib/utils/cn';

export function RailContextHeader() {
  const { entityType, selectedNode } = useViewportSelection();

  if (!entityType || !selectedNode) return null;
  const props = (selectedNode.properties ?? {}) as Record<string, unknown>;

  const typeLabel = readString(props.header_type_label);
  const primary = readString(props.header_primary) ?? selectedNode.label?.trim();
  const subline = readString(props.header_subline);

  if (!primary) return null;

  return (
    <div
      className={cn(
        'sticky top-0 z-10 -mt-2 -mx-2 mb-2 px-3 py-2',
        'bg-panel/95 backdrop-blur-sm border-b border-border',
        'flex items-start gap-2',
      )}
    >
      {typeLabel && (
        <span className="shrink-0 text-[10px] uppercase tracking-wider font-medium px-1.5 py-0.5 rounded bg-primary/10 text-primary">
          {typeLabel}
        </span>
      )}
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium truncate" title={selectedNode.id ?? undefined}>
          {primary}
        </p>
        {subline && (
          <p className="text-[11px] text-muted-foreground truncate">{subline}</p>
        )}
      </div>
    </div>
  );
}

function readString(v: unknown): string | null {
  return typeof v === 'string' && v.trim() ? v.trim() : null;
}
